import { useEffect, useState } from "react";
import { Loader2, Share2, Trophy, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { vaultSpin, vaultReveal, vaultRevealMs, vaultKey } from "@/lib/vaultSounds";
import { VaultWinOverlay } from "@/components/vault/VaultWinOverlay";
import { VaultDuelPanel } from "@/components/vault/VaultDuelPanel";
import { LiveDuels } from "@/components/vault/DuelCheers";
import { useGames, gameOn, gameBlocked } from "@/lib/games";
import { UserAvatar, UserLink } from "@/components/UserBits";
import { VaultHeader } from "@/components/vault/VaultHeader";
import { VaultPad } from "@/components/vault/VaultPad";
import { VaultLocked } from "@/components/vault/VaultLocked";
import { VaultHistory, VaultNotes } from "@/components/vault/VaultHistory";
import { VaultFriends } from "@/components/vault/VaultFriends";
import { VaultShare } from "@/components/vault/VaultShare";

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

function Rules({ consolation }) {
  return (
    <details className="rounded-3xl bg-slate-900 text-white/80 p-5 ring-1 ring-white/10 text-sm" data-testid="vault-rules">
      <summary className="font-extrabold text-white cursor-pointer flex items-center gap-2"><HelpCircle className="w-4 h-4 text-amber-300" /> So funktioniert der Tresor</summary>
      <ul className="mt-3 space-y-1.5 list-disc pl-5">
        <li>Der Tresor hat einen geheimen Code aus 4 verschiedenen Ziffern. Alle in der Community knacken denselben Tresor.</li>
        <li>Jeder Versuch kostet einen Schlüssel. Du bekommst jeden Tag neue Schlüssel.</li>
        <li><b className="text-amber-300">Gold</b> = richtige Ziffer am richtigen Platz. <b className="text-slate-200">Silber</b> = die Ziffer kommt vor, aber an einer anderen Stelle. Welche Ziffer gemeint ist, verrät der Tresor nicht.</li>
        <li>Mit jedem Versuch wächst der Topf. Wer den Code zuerst knackt, gewinnt alles – danach öffnet sofort ein neuer Tresor.</li>
        <li>Wer beim Knacken schon 3 von 4 richtig hatte, bekommt {consolation} Trostpunkte. Wer täglich spielt, bekommt einen wachsenden Serien-Bonus.</li>
        <li>Es geht nur um Punkte, nie um echtes Geld. Eine Pause kostet dich keine Punkte.</li>
      </ul>
    </details>
  );
}

function Winners({ history }) {
  if (!history.length) return null;
  return (
    <div className="rounded-3xl bg-slate-900 text-white p-5 ring-1 ring-white/10" data-testid="vault-winners">
      <h3 className="font-display text-lg font-extrabold flex items-center gap-2 mb-3"><Trophy className="w-5 h-5 text-amber-300" /> Zuletzt geknackt</h3>
      <ul className="space-y-2">
        {history.map((h) => (
          <li key={h.no} className="flex items-center gap-3 text-sm" data-testid={`vault-winner-${h.no}`}>
            {h.winner && <UserAvatar userId={h.winner.user_id} name={h.winner.name} picture={h.winner.picture} size="w-9 h-9" text="text-xs" />}
            <div className="min-w-0 flex-1"><UserLink id={h.winner?.user_id} className="font-bold text-white">{h.winner?.name || "Mitglied"}</UserLink><p className="text-xs text-white/50">Tresor #{h.no} · Code {h.code} · {h.tries} Versuche</p></div>
            <span className="font-black text-amber-300">+{h.pot}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function VaultGame() {
  const { user, refreshUser } = useAuth();
  const [s, setS] = useState(null);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);
  const [win, setWin] = useState(null);
  const [share, setShare] = useState(false);
  const [overlay, setOverlay] = useState(false);
  const [door, setDoor] = useState({ angle: 0, lit: 0, spinning: false });
  const [fresh, setFresh] = useState(null);
  const games = useGames();
  const load = () => api.get("/vault").then(({ data }) => setS(data)).catch((e) => toast.error(formatErr(e)));
  useEffect(() => {
    load();
    const iv = setInterval(() => document.visibilityState === "visible" && load(), 20000);
    return () => clearInterval(iv);
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const guess = value;
      setDoor((d) => ({ angle: d.angle + 720 + Math.round(Math.random() * 360), lit: 0, spinning: true }));
      vaultSpin(950);
      const [{ data }] = await Promise.all([api.post("/vault/guess", { guess }), wait(980)]);
      setDoor((d) => ({ ...d, spinning: false, lit: data.won ? 4 : data.gold }));
      if (data.won) { setWin(data); setOverlay(true); }
      else {
        setFresh(guess);
        vaultReveal(data.gold, data.silver);
        if (!data.gold && !data.silver) { setShake(true); setTimeout(() => setShake(false), 500); }
        setTimeout(() => toast(data.gold + data.silver ? `${data.gold} Gold · ${data.silver} Silber – der Topf wächst auf ${data.pot}` : "Keine dieser Ziffern ist im Code – auch das ist ein Hinweis!"), vaultRevealMs(data.gold, data.silver));
      }
      if (data.bonus) setTimeout(() => toast.success(`Serien-Bonus: +${data.bonus} Punkte 🔥`), 900);
      setValue(""); refreshUser?.(); await load();
    } catch (e) { toast.error(formatErr(e)); await load(); }
    finally { setBusy(false); }
  };

  if (!s) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;
  return (
    <div className="space-y-4" data-testid="vault-game">
      {gameOn(games, "vault_duel") && <LiveDuels />}
      <VaultHeader s={s} />
      {win && (
        <div className="rounded-3xl p-5 text-center bg-gradient-to-br from-amber-300 to-amber-500 text-slate-900 vault-pop" data-testid="vault-win">
          <p className="font-display text-2xl font-black">Du hast den Tresor geknackt! 🔓</p>
          <p className="text-4xl font-black mt-1">+{win.pot} Punkte</p>
          <p className="text-sm font-bold mt-1">Der Code war {win.code}. Der neue Tresor öffnet um 9 Uhr.</p>
          <button onClick={() => setShare(true)} data-testid="vault-share-btn" className="mt-3 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-slate-900 text-amber-300 font-extrabold"><Share2 className="w-4 h-4" /> Gewinn teilen</button>
        </div>
      )}
      <div className="rounded-3xl bg-slate-900 text-white p-5 space-y-5 ring-1 ring-white/10">
        {s.round.locked && <VaultLocked at={s.round.opens_at} onOpen={load} round={s.round} reload={load} />}
        <VaultPad value={value} setValue={setValue} onSubmit={submit} busy={busy} disabled={s.keys.left <= 0 || s.round.locked} shake={shake} door={door} />
        {!s.round.locked && s.keys.left <= 0 && <p className="text-center text-sm font-bold text-amber-200" data-testid="vault-no-keys">Keine Schlüssel mehr für heute – morgen gibt's neue. Oder lass dir einen von Freunden schenken!</p>}
        <VaultNotes key={s.round.no} roundNo={s.round.no} />
        <VaultHistory mine={s.mine} fresh={fresh} />
      </div>
      {gameOn(games, "vault_duel") && !gameBlocked(games, "vault_duel") && <VaultDuelPanel />}
      <VaultFriends onGift={() => { vaultKey(); load(); }} />
      <Winners history={s.history} />
      <Rules consolation={s.consolation} />
      {overlay && win && <VaultWinOverlay win={win} onShare={() => { setOverlay(false); setShare(true); }} onClose={() => setOverlay(false)} />}
      {share && win && <VaultShare win={win} user={user} onClose={() => setShare(false)} />}
    </div>
  );
}
